import {useEffect, useRef, useState} from 'react'

import {CHAR_SPEED, POST_TYPE_WAIT, REDACT_DURATION} from '../constants'
import {drawRedaction} from '../utils'
import {ProgressBar} from './ProgressBar'

export const PhaseTransition = ({
  completedPhase: _completedPhase,
  questionNumber,
  totalQuestions,
  onComplete,
}: {
  completedPhase: 1 | 2
  questionNumber: number
  totalQuestions: number
  onComplete: () => void
}) => {
  const [fadedIn, setFadedIn] = useState(false)
  const [fadingOut, setFadingOut] = useState(false)
  const [slideStarted, setSlideStarted] = useState(false)
  const [typedChars, setTypedChars] = useState(0)
  const [showRedaction, setShowRedaction] = useState(false)
  const keywordRef = useRef<HTMLSpanElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  const transitionText = 'The case is building.\nEntering phase 2.'
  const keyword = 'case is building'
  const keywordStart = transitionText.indexOf(keyword)

  // Orchestrate entry animations: fade-in → slide + typewriter → redaction → auto-advance
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = []

    // Delay before content fades in
    timers.push(setTimeout(() => setFadedIn(true), 200))

    // Start slide after fade-in begins
    timers.push(setTimeout(() => setSlideStarted(true), 1200))

    // Start typewriter at same time as slide
    const typeStart = 1200

    for (let i = 1; i <= transitionText.length; i++) {
      timers.push(setTimeout(() => setTypedChars(i), typeStart + i * CHAR_SPEED))
    }

    // Redact after typewriter finishes
    const typeEnd = typeStart + transitionText.length * CHAR_SPEED
    timers.push(setTimeout(() => setShowRedaction(true), typeEnd + POST_TYPE_WAIT))

    // Fade out after redaction + pause
    const fadeOutAt = typeEnd + POST_TYPE_WAIT + REDACT_DURATION + 800
    timers.push(setTimeout(() => setFadingOut(true), fadeOutAt))

    // Auto-advance after fade-out completes (700ms matches transition duration)
    timers.push(setTimeout(() => onCompleteRef.current(), fadeOutAt + 700))

    return () => timers.forEach(clearTimeout)
  }, [])

  // Position canvas over keyword and run redaction animation
  useEffect(() => {
    if (!showRedaction || !canvasRef.current || !keywordRef.current) {
      return
    }

    const span = keywordRef.current
    const canvas = canvasRef.current
    canvas.width = span.offsetWidth + 8
    canvas.height = span.offsetHeight
    canvas.style.left = `${span.offsetLeft - 4}px`
    drawRedaction(canvas)
  }, [showRedaction])

  return (
    <div className="flex min-h-screen flex-col px-6 py-8 md:mx-auto md:w-full md:max-w-3xl">
      {/* ── Progress bar (top) ── */}
      <div className="mb-10">
        <ProgressBar current={questionNumber} total={totalQuestions} />
      </div>

      {/* ── Content area (same h-[520px] as question screens) ── */}
      <div
        className="flex h-[520px] flex-col items-center pt-[48px] transition-opacity duration-700 ease-in-out"
        style={{opacity: fadingOut || !fadedIn ? 0 : 1}}
      >
        {/* Illustration — matches Figma proportions */}
        <div className="relative h-[236px] w-[410px] origin-top scale-90">
          {/* ── Folder (centered) ── */}

          {/* Back panel with tab */}
          <div className="absolute left-[124px] top-0 h-[236px] w-[163px]">
            {/* Tab */}
            {/* Back body (visible as shadow on right + bottom) */}
            <div className="absolute left-[3px] top-[14px] h-[226px] w-[158px] rounded-[3px] bg-[#3d3c3c]" />
          </div>

          {/* Tab */}
          <div className="absolute left-[138px] top-[2px] z-[1] h-[14px] w-[65px] rounded-t-[3px] border border-b-0 border-[#5b5858] bg-[#3d3c3c]" />

          {/* Front panel */}
          <div className="absolute left-[124px] top-[10px] z-[2] flex h-[226px] w-[157px] flex-col justify-end rounded-[3px] border border-[#3d3c3c] bg-[#1f1f1f] px-[12px] pb-[14px]">
            <span className="text-[8px] font-medium text-ui-red-450">Internal - Do not distribute</span>
            <span className="mt-[2px] text-[12px] font-medium text-moss-white">Case File 01</span>
            <div className="mt-[8px] h-[5px] w-[70%] rounded-[1.5px] bg-[#3d3c3c]" />
            <div className="mt-[4px] h-[5px] w-[55%] rounded-[1.5px] bg-[#3d3c3c]" />
          </div>

          {/* ── Document (right) — slides behind folder ── */}
          <div
            className="absolute left-[297px] top-[16px]"
            style={{
              animation: slideStarted ? 'slide-behind 1s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
            }}
          >
            <div className="h-[214px] w-[152px] rounded-[3px] border border-[#3d3c3c] bg-[#1f1f1f] px-[12px] pt-[14px]">
              {/* Placeholder bars */}
              <div className="mb-[4px] h-[7px] w-[59px] rounded-[1.5px] bg-[#3d3c3c]" />
              <div className="mb-[4px] h-[7px] w-[42px] rounded-[1.5px] bg-[#3d3c3c]" />
              <div className="mb-[4px] h-[7px] w-[76px] rounded-[1.5px] bg-[#3d3c3c]" />
              <div className="mb-[18px] h-[7px] w-[59px] rounded-[1.5px] bg-[#85b82e]" />
              <div className="mb-[4px] h-[7px] w-[97px] rounded-[1.5px] bg-[#3d3c3c]" />
              <div className="h-[7px] w-[42px] rounded-[1.5px] bg-[#3d3c3c]" />
            </div>
          </div>
        </div>

        {/* Text below illustration — typewriter + redaction */}
        <p className="font-typewriter relative mt-4 whitespace-pre-line text-center text-[16px] leading-[24px] text-grey-450">
          {typedChars > 0 && (
            <>
              {typedChars <= keywordStart && transitionText.slice(0, typedChars)}
              {typedChars > keywordStart && (
                <>
                  {transitionText.slice(0, keywordStart)}
                  <span ref={keywordRef}>{keyword.slice(0, Math.min(typedChars - keywordStart, keyword.length))}</span>
                  {typedChars > keywordStart + keyword.length &&
                    transitionText.slice(keywordStart + keyword.length, typedChars)}
                </>
              )}
            </>
          )}
          {typedChars > 0 && (
            <span className="text-green-neon" style={{animation: 'blink 1s step-end infinite'}}>
              |
            </span>
          )}
          <canvas
            ref={canvasRef}
            height={28}
            className="pointer-events-none absolute top-0"
            style={{display: showRedaction ? 'block' : 'none'}}
          />
        </p>
      </div>

      {/* ── Bottom bar (same spacing as question screens) ── */}
      <div className="pt-4" />
    </div>
  )
}
